import type { TFunction } from 'i18next';

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

// Everything is dated in the partner's timezone rather than the browser's, so a late-evening
// item doesn't land on a different day for someone abroad.
const TIME_ZONE = 'Europe/Zurich';

// Swiss dd.MM.yyyy regardless of the UI language.
export function formatSwissDate(date: Date): string {
    return date.toLocaleDateString('de-CH', {
        timeZone: TIME_ZONE,
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
}

// en-CA is the shortest way to get a sortable YYYY-MM-DD out of Intl.
export function calendarDay(date: Date): string {
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: TIME_ZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(date);
}

export function formatRelativeTime(iso: string, t: TFunction, now: Date = new Date()): string {
    const then = new Date(iso);
    const thenMs = then.getTime();
    if (Number.isNaN(thenMs)) {
        return t('TIME.UNKNOWN');
    }

    const diff = Math.max(0, now.getTime() - thenMs);

    if (diff < MINUTE_MS) {
        return t('TIME.JUST_NOW');
    }
    if (diff < HOUR_MS) {
        return t('TIME.MINUTES_AGO', { count: Math.floor(diff / MINUTE_MS) });
    }
    if (diff < DAY_MS) {
        return t('TIME.HOURS_AGO', { count: Math.floor(diff / HOUR_MS) });
    }
    if (diff < 2 * DAY_MS) {
        return t('TIME.YESTERDAY');
    }
    if (diff < 7 * DAY_MS) {
        return t('TIME.DAYS_AGO', { count: Math.floor(diff / DAY_MS) });
    }
    return formatSwissDate(then);
}

// `day` is a key from `calendarDay`. The weekday comes from the browser, already translated.
export function formatDayLabel(day: string, t: TFunction, language: string, now: Date = new Date()): string {
    if (day === calendarDay(now)) {
        return t('TIME.DAY_TODAY');
    }

    const date = new Date(`${day}T00:00:00Z`);
    const swissDate = formatSwissDate(date);

    const yesterday = new Date(now.getTime() - DAY_MS);
    if (day === calendarDay(yesterday)) {
        return t('TIME.DAY_YESTERDAY', { date: swissDate });
    }

    const weekday = date.toLocaleDateString(language, { timeZone: TIME_ZONE, weekday: 'long' });
    return t('TIME.DAY_WEEKDAY', { weekday, date: swissDate });
}
